import React, { useState } from "react";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import { company, interests } from "../mock";
import { useToast } from "../hooks/use-toast";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({
        title: "A couple of details missing",
        description: "Please share your name and email so we can get back to you.",
      });
      return;
    }
    toast({
      title: "Message received",
      description: "Thank you. We\u2019ll be in touch within one working day.",
    });
    setForm({ name: "", email: "", phone: "", interest: "", message: "" });
  };

  return (
    <section id="contact" className="section-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow eyebrow-line">Start a Conversation</p>
            <h2 className="font-serif-display mt-6 text-[2.1rem] md:text-[3rem] leading-[1.05] text-[#2a3325]">
              A <span className="italic-accent">calmer</span>, better-run portfolio.
            </h2>
            <p className="mt-6 text-[#4d5a45] leading-relaxed max-w-md">
              Send us a brief note and we’ll reply within one working day. Every conversation stays private, and the introductory call is offered without fee or commitment.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <a href={`mailto:${company.email}`} className="group block border-t border-[#c4cfb2] pt-5">
                <Mail className="w-5 h-5 text-[#8a9a6b]" strokeWidth={1.4} />
                <p className="eyebrow mt-4">Email</p>
                <p className="font-serif-display text-lg text-[#2a3325] mt-2 group-hover:text-[#8a9a6b] transition-colors break-all">{company.email}</p>
              </a>
              <a href={`tel:${company.phoneHref}`} className="group block border-t border-[#c4cfb2] pt-5">
                <Phone className="w-5 h-5 text-[#8a9a6b]" strokeWidth={1.4} />
                <p className="eyebrow mt-4">Phone</p>
                <p className="font-serif-display text-lg text-[#2a3325] mt-2 group-hover:text-[#8a9a6b] transition-colors">{company.phone}</p>
              </a>
              <div className="border-t border-[#c4cfb2] pt-5">
                <MapPin className="w-5 h-5 text-[#8a9a6b]" strokeWidth={1.4} />
                <p className="eyebrow mt-4">Registered Office</p>
                <p className="font-serif-display text-[0.98rem] text-[#2a3325] mt-2 leading-snug">
                  {company.address.line1}<br />{company.address.line2}
                </p>
              </div>
              <div className="border-t border-[#c4cfb2] pt-5">
                <p className="eyebrow mt-9">Working Focus</p>
                <p className="font-serif-display text-[0.98rem] text-[#2a3325] mt-2">
                  London W1D · Tier-1 metropolises
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} className="bg-[#dde3d0] border border-[#c4cfb2] rounded-sm p-8 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Your name *">
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#a9b891] rounded-none focus-visible:ring-0 focus-visible:border-[#2a3325] px-0"
                    placeholder=""
                  />
                </Field>
                <Field label="Email *">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#a9b891] rounded-none focus-visible:ring-0 focus-visible:border-[#2a3325] px-0"
                  />
                </Field>
                <Field label="Phone (optional)">
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#a9b891] rounded-none focus-visible:ring-0 focus-visible:border-[#2a3325] px-0"
                  />
                </Field>
                <Field label="Area of interest">
                  <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                    <SelectTrigger className="bg-transparent border-0 border-b border-[#a9b891] rounded-none focus:ring-0 px-0 text-[#2a3325]">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {interests.map((i) => (
                        <SelectItem key={i} value={i}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="mt-6">
                <Field label="Brief message">
                  <Textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#a9b891] rounded-none focus-visible:ring-0 focus-visible:border-[#2a3325] px-0 resize-none"
                  />
                </Field>
              </div>

              <p className="text-xs text-[#6b7860] mt-6 leading-relaxed">
                By sending this form, you agree to be contacted about your enquiry. Your details are never shared with any third party.
              </p>

              <button type="submit" className="btn-dark mt-8">
                Send Enquiry <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
